/* eslint-disable max-lines */
import * as React from "react";
import { connect } from "react-redux";
import { values } from "lodash";
import * as moment from "moment";

import { getQuestionConfig } from "@teamprevent/common/questionnaires/questionnaireConfig";
import { mapDispatchToQuestionnaireSectionProps, makeMapStateToQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/mappers";
import { CustomQuestionsQuestionnaireSectionProps } from "@teamprevent/common/questionnaires/QuestionnaireSectionProps";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { OldDateQuestion } from "@teamprevent/common/questionnaires/components/questions/OldDateQuestion";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";
import { NumberQuestion } from "@teamprevent/common/questionnaires/components/questions/NumberQuestion";
import { MultiButtonQuestion } from "@teamprevent/common/questionnaires/components/questions/MultiButtonQuestion";
import { getAnswerToQuestionWithId } from "@teamprevent/common/questionnaires/getAnswerToQuestionWithId";
import { MultiCheckboxQuestion, MultiCheckbox } from "@teamprevent/common/questionnaires/components/questions/MultiCheckboxQuestion";
import { YesNoWithFollowUpQuestion } from "@teamprevent/common/questionnaires/components/questions/YesNoWithFollowUpQuestion";
import { CheckboxQuestion } from "@teamprevent/common/questionnaires/components/questions/CheckboxQuestion";

import { ContextAwareProgressBarQuestionnaireSection } from "@teamprevent/common/questionnaires/components/ContextAwareProgressBarQuestionnaireSection";

import { ApplicationState } from "../../applicationState";
import * as questionIds from "../consultationQuestions/questionIds";
import { distanceTestResultOptions, eyeVisionPeripheralFieldOptions, intermediateNearTestResultOptions, visionHorizontalFieldOptions } from "../models";
import { sections } from "../consultation";

const otherTestTypeAnswer = "Other";
const otherRoleTypeAnswer = "Other";
const noVisualAidAnswer = "None";
const otherVisualAidAnswer = "Other visual aid";

interface AdditionalProps {
    employeeDob: moment.Moment;
}

type AllProps = CustomQuestionsQuestionnaireSectionProps & AdditionalProps;

const HealthSurveillanceConsultationVisionSectionUnconnected: React.FunctionComponent<AllProps> = (props: AllProps) => {

    const questions = values(props.questions || {});

    return (
        <ContextAwareProgressBarQuestionnaireSection
            {...props}
            {...sections.Vision}
        >
            {YesNoWithFollowUpQuestion({
                readonly: props.readonly,
                questionConfigs: [
                    getQuestionConfig(props.questionConfigs, questionIds.VisionUnableToCarryOut),
                    getQuestionConfig(props.questionConfigs, questionIds.VisionUnableToCarryOutReason)
                ],
                mandatory: true,
                followUpMandatory: false,
                questions: props.questions
            })}
            {getAnswerToQuestionWithId(questions, questionIds.VisionUnableToCarryOut) !== MultiButtonQuestion.YesOption && <>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionTestType)}
                    options={[
                        { text: "Keystone", value: "Keystone" },
                        { text: "Titmus", value: "Titmus" },
                        { text: "Snellen Chart", value: "Snellen Chart" },
                        { text: otherTestTypeAnswer, value: otherTestTypeAnswer }
                    ]}
                />
                <OldDateQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionServiceDate)}
                />
                <NumberQuestion
                    readonly
                    answer={props.employeeDob && moment().diff(props.employeeDob, "years")}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionAge)}
                    validationDependencies={[getAnswerToQuestionWithId(questions, questionIds.VisionAge)]}
                />
                <MultiCheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRoleType)}
                >
                    <MultiCheckbox label="Office based - VDU" value="Office based - VDU" />
                    <MultiCheckbox label="Office based + driving on company business" value="Office based + driving on company business" />
                    <MultiCheckbox label="Lift truck driver" value="Lift truck driver" />
                    <MultiCheckbox label="Crate operator" value="Crate operator" />
                    <MultiCheckbox label="DVLA Group 1 - cars" value="DVLA Group 1 - cars" />
                    <MultiCheckbox label="Group 2 LGV (HGV)" value="Group 2 LGV (HGV)" />
                    <MultiCheckbox label="PCV" value="PCV" />
                    <MultiCheckbox label="Other" value={otherRoleTypeAnswer} />
                </MultiCheckboxQuestion>
                {getAnswerToQuestionWithId(questions, questionIds.VisionRoleType) === otherRoleTypeAnswer &&
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionOtherRoleType)}
                    />
                }
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLastOpticianTest)}
                    options={[
                        { text: "Within past two years (in date)", value: "Within past two years (in date)" },
                        {
                            text: "> 2 years; advise attendance at opticians for eye health",
                            value: "> 2 years; advise attendance at opticians for eye health"
                        },
                        {
                            text: "Never had opticians test; advise attendance at opticians for eye health",
                            value: "Never had opticians test; advise attendance at opticians for eye health"
                        },
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionCurrentCorrectionRequired)}
                    options={[
                        { text: noVisualAidAnswer, value: noVisualAidAnswer },
                        { text: "Glasses", value: "Glasses" },
                        { text: "Contacts", value: "Contacts" },
                        { text: "Glasses and Contacts", value: "Glasses and Contacts" },
                        { text: "Has had laser correction", value: "Has had laser correction" },
                        { text: "Cataract surgery", value: "Cataract surgery" },
                        { text: otherVisualAidAnswer, value: otherVisualAidAnswer }
                    ]}
                />
                {getAnswerToQuestionWithId(questions, questionIds.VisionCurrentCorrectionRequired) === otherVisualAidAnswer &&
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionCurrentVisualAidDescription)}
                    />
                }
                {!!getAnswerToQuestionWithId(questions, questionIds.VisionCurrentCorrectionRequired) &&
                    getAnswerToQuestionWithId(questions, questionIds.VisionCurrentCorrectionRequired) !== noVisualAidAnswer &&
                    <DropdownQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionCorrectivePrescriptionUsed)}
                        options={[
                            { text: "Distance", value: "Distance" },
                            { text: "Intermediate", value: "Intermediate - VDU distance" },
                            { text: "Reading", value: "Reading distance" },
                            { text: "Varifocals", value: "Varifocals" },
                            { text: "Occupational Lenses (Multifocals)", value: "Occupational lenses" }
                        ]}
                    />
                }
                <MultiCheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionFamilyHistory)}
                >
                    <MultiCheckbox label="Glaucoma advice regular opticians test" value="Glaucoma advice regular opticians test" />
                    <MultiCheckbox label="Macular degeneration" value="Macular degeneration" />
                    <MultiCheckbox label="Neither of the above" value="Neither of the above" />
                </MultiCheckboxQuestion>
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHistoricalCurrentIssues)}
                    outdated={{
                        isOutdated: true,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.Vision.id
                    }}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHistoricalCurrentIssuesYesOrNo)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.Vision.id
                    }}
                />
                {getAnswerToQuestionWithId(questions, questionIds.VisionHistoricalCurrentIssuesYesOrNo) === MultiButtonQuestion.YesOption && <>
                    <TextQuestion
                        readonly={props.readonly}
                        questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHistoricalCurrentIssuesDetails)}
                        mandatory
                        multiline
                        outdated={{
                            isOutdated: false,
                            questionnaireKey: props.questionnaireKey,
                            sectionId: sections.Vision.id
                        }}
                    />
                </>}
                <p>Distance Vision Result</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDistanceCorrectionDistance)}
                    options={[{ text: "Corrected", value: "Corrected" }, { text: "Uncorrected", value: "Uncorrected" }]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRightEyeDistance)}
                    options={distanceTestResultOptions}
                    filterOldOptions
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLeftEyeDistance)}
                    options={distanceTestResultOptions}
                    filterOldOptions
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesDistance)}
                    options={distanceTestResultOptions}
                    filterOldOptions
                />
                <p>Intermediate Result</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionIntermediateCorrection)}
                    options={[{ text: "Corrected", value: "Corrected" }, { text: "Uncorrected", value: "Uncorrected" }]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesIntermediate100Cm)}
                    options={intermediateNearTestResultOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesIntermediate60Cm)}
                    options={intermediateNearTestResultOptions}
                />
                <p>Near Vision Result</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDistanceCorrectionNear)}
                    options={[{ text: "Corrected", value: "Corrected" }, { text: "Uncorrected", value: "Uncorrected" }]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRightEyeNear)}
                    options={intermediateNearTestResultOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLeftEyeNear)}
                    options={intermediateNearTestResultOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionBothEyesNear)}
                    options={intermediateNearTestResultOptions}
                />
                <p>Night Vision</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionNightVision)}
                    options={[
                        { text: "Acceptable", value: "Acceptable" },
                        {
                            text: "Marginal or unacceptable result; advise employee of impact on driving at night and refer to optician",
                            value: "Marginal or unacceptable result; advise employee of impact on driving at night and refer to optician"
                        }
                    ]}
                />
                <p>Colour Vision</p>
                <MultiCheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionColourTestType)}
                >
                    <MultiCheckbox label="Keystone" value="Keystone" />
                    <MultiCheckbox label="Ishihara" value="Ishihara" />
                    <MultiCheckbox label="City University" value="City University" />
                    <MultiCheckbox label="Trade Test" value="Trade Test" />
                </MultiCheckboxQuestion>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionColourResult)}
                    options={[
                        { text: "Three correct: Acceptable", value: "Three correct: Acceptable" },
                        {
                            text: "Two correct: Marginal, may need trade test if in safety critical role e.g. electrician, or trackside",
                            value: "Two correct: Marginal, may need trade test if in safety critical role e.g. electrician, or trackside"
                        },
                        {
                            text: "None or one correct: Unacceptable, refer if safety critical",
                            value: "None or one correct: Unacceptable, refer if safety critical"
                        }
                    ]}
                />
                <p>Peripheral Field of Vision</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionLeftEyePeripheralField)}
                    options={eyeVisionPeripheralFieldOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRightEyePeripheralField)}
                    options={eyeVisionPeripheralFieldOptions}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionHorizontalField)}
                    options={visionHorizontalFieldOptions}
                />
                <p>Phoria (Eye co-ordination)</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionPhoriaDistance)}
                    options={[
                        { text: "Satisfactory", value: "Satisfactory" },
                        {
                            text: "If outwith parameters and symptoms of headache etc. present, advise optician review",
                            value: "If outwith parameters and symptoms of headache etc. present, advise optician review"
                        }
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionPhoriaNear)}
                    options={[
                        { text: "Satisfactory", value: "Satisfactory" },
                        {
                            text: "If outwith parameters and symptoms of headache etc. present, advise optician review",
                            value: "If outwith parameters and symptoms of headache etc. present, advise optician review"
                        }
                    ]}
                />
                <p>Depth Perception (Crane Ops/LiftTrucks/NDT etc.)</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDepthPerceptionDistance)}
                    options={[
                        { text: "All correct: Acceptable", value: "All correct: Acceptable" },
                        {
                            // eslint-disable-next-line max-len
                            text: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing",
                            // eslint-disable-next-line max-len
                            value: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing"
                        }
                    ]}
                />
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionDepthPerceptionNear)}
                    options={[
                        { text: "All correct: Acceptable", value: "All correct: Acceptable" },
                        {
                            // eslint-disable-next-line max-len
                            text: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing",
                            // eslint-disable-next-line max-len
                            value: "Marginal or unacceptable result, refer to optician if safety critical role e.g. close visual fault inspection/non destructive testing"
                        }
                    ]}
                />
                <p>Client Guidance</p>
                <DropdownQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionSafetyGlassesAdequateAndInGoodCondition)}
                    options={[
                        { text: "Yes", value: "Yes" },
                        { text: "No - refer to line manager", value: "No - refer to line manager" },
                        { text: "Not available for inspection", value: "Not available for inspection" }
                    ]}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.VisionRemindedToAttendOpticiansRoutinely)}
                />
                <MultiButtonQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.OpticiansSelfReferralLetterIssued)}
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.Vision.id
                    }}
                />
                <CheckboxQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EmployeeInformedGlassesAreWornToMeetMinimumStandards)}
                    value="Yes"
                    mandatory
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.Vision.id
                    }}
                />
                <TextQuestion
                    readonly={props.readonly}
                    questionConfig={getQuestionConfig(props.questionConfigs, questionIds.EmployeeInformedGlassesAreWornToMeetMinimumStandardsAdditionalNotes)}
                    outdated={{
                        isOutdated: false,
                        questionnaireKey: props.questionnaireKey,
                        sectionId: sections.Vision.id
                    }}
                    multiline
                />
            </>}
        </ContextAwareProgressBarQuestionnaireSection>
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: any) => {
    return {
        ...makeMapStateToQuestionnaireSectionProps(sections.Vision.id)(state, ownProps)
    };
};

export const HealthSurveillanceConsultationVisionSection = connect(
    mapStateToProps,
    mapDispatchToQuestionnaireSectionProps
)(HealthSurveillanceConsultationVisionSectionUnconnected);
